import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Save, ArrowLeft, Upload, Image as ImageIcon, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEventForm = () => {
  const { isAdmin, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;

  const [formData, setFormData] = useState({
    title_it: '',
    title_en: '',
    title_de: '',
    title_fr: '',
    title_es: '',
    description_it: '',
    description_en: '',
    description_de: '',
    description_fr: '',
    description_es: '',
    date_start: '',
    date_end: '',
    location: '',
    country: '',
    price_rent: '',
    price_own: '',
    price_rzr_single: '',
    price_rzr_double: '',
    image_url: '',
    event_type: 'enduring',
    status: 'upcoming',
    spots_available: '',
    spots_total: '',
    featured: false,
    itinerary: '',
    sumup_url: '',
    deposit_amount: '',
    single_room_supplement: ''
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showGallery, setShowGallery] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  // Fetch countries for dropdown
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API}/countries`);
        setCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  const openGallery = async () => {
    setShowGallery(true);
    setGalleryLoading(true);
    try {
      const adminPassword = localStorage.getItem('adminPassword') || 'offroad2026';
      const resp = await axios.get(`${API}/uploads`, {
        headers: { 'X-Admin-Password': adminPassword },
      });
      setGalleryItems(resp.data);
    } catch (err) {
      console.error('Gallery load error:', err);
      setGalleryItems([]);
    } finally {
      setGalleryLoading(false);
    }
  };

  const pickFromGallery = (item) => {
    setFormData(prev => ({ ...prev, image_url: item.image_url }));
    setShowGallery(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');

    // Client-side validation
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setUploadError('Formato non supportato. Usa JPG, PNG, WEBP o GIF.');
      e.target.value = '';
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setUploadError('File troppo grande (max 15MB).');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const adminPassword = localStorage.getItem('adminPassword') || 'offroad2026';
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/uploads/image`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Password': adminPassword,
        },
      });
      setFormData(prev => ({ ...prev, image_url: resp.data.image_url }));
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.response?.data?.detail || 'Errore durante il caricamento. Riprova.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (authLoading) return; // Wait for auth to restore
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }

    if (isEdit) {
      fetchEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isEdit, eventId, authLoading]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
      alert('Errore nel caricamento dell\'evento');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Converti stringhe vuote a null per i prezzi
      const dataToSend = {
        ...formData,
        price_rent: formData.price_rent ? parseFloat(formData.price_rent) : null,
        price_own: formData.price_own ? parseFloat(formData.price_own) : null,
        price_rzr_single: formData.price_rzr_single ? parseFloat(formData.price_rzr_single) : null,
        price_rzr_double: formData.price_rzr_double ? parseFloat(formData.price_rzr_double) : null,
        spots_available: formData.spots_available ? parseInt(formData.spots_available) : null,
        spots_total: formData.spots_total ? parseInt(formData.spots_total) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        single_room_supplement: formData.single_room_supplement ? parseFloat(formData.single_room_supplement) : null
      };

      if (isEdit) {
        await axios.put(`${API}/events/${eventId}`, dataToSend);
      } else {
        await axios.post(`${API}/events`, dataToSend);
      }
      
      // Mostra messaggio di successo
      setSuccess(true);
      
      // Navigare dopo un piccolo delay per evitare errori React
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Errore durante il salvataggio: ' + (error.response?.data?.detail || error.message));
      setSaving(false);
    }
  };

  if (!isAdmin || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Torna alla Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Modifica Evento' : 'Nuovo Evento'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 flex items-center justify-center">
              <div className="flex items-center">
                <div className="bg-green-500 text-white rounded-full p-2 mr-4">
                  <Save size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-800">
                    {isEdit ? 'Evento Aggiornato!' : 'Evento Creato!'}
                  </h3>
                  <p className="text-green-700">Reindirizzamento alla dashboard...</p>
                </div>
              </div>
            </div>
          )}

          {/* Informazioni Base */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Informazioni Base</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo (Italiano) *
                </label>
                <input
                  type="text"
                  name="title_it"
                  value={formData.title_it}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Es: Tunisia - Trois Desert"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo (English)
                </label>
                <input
                  type="text"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo (Deutsch)
                </label>
                <input
                  type="text"
                  name="title_de"
                  value={formData.title_de}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo (Français)
                </label>
                <input
                  type="text"
                  name="title_fr"
                  value={formData.title_fr}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo (Español)
                </label>
                <input
                  type="text"
                  name="title_es"
                  value={formData.title_es}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Evento *
                </label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="enduring">Enduring</option>
                  <option value="tour">Tour</option>
                  <option value="corso">Corso</option>
                  <option value="desert">Desert Experience</option>
                  <option value="rally">Rally</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stato *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="upcoming">Prossimi</option>
                  <option value="sold_out">Sold Out</option>
                  <option value="past">Passati</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Mostra in evidenza sulla homepage
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Date e Luogo */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Date e Luogo</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Inizio *
                </label>
                <input
                  type="text"
                  name="date_start"
                  value={formData.date_start}
                  onChange={handleChange}
                  required
                  placeholder="01/02/2026"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Fine *
                </label>
                <input
                  type="text"
                  name="date_end"
                  value={formData.date_end}
                  onChange={handleChange}
                  required
                  placeholder="08/02/2026"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Luogo *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="Tunisia - Djerba"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazione * <span className="text-gray-500 font-normal">(determina sotto quale bandiera appare l'evento)</span>
                </label>
                <select
                  name="country"
                  value={formData.country || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="event-country-select"
                >
                  <option value="">— Seleziona la nazione —</option>
                  {countries.map(c => (
                    <option key={c.id} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Gestisci le nazioni da: <Link to="/admin/countries" className="text-blue-600 underline">Admin → Nazioni</Link>
                </p>
              </div>
            </div>
          </div>

          {/* Prezzi */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Prezzi (in Euro)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzo con Noleggio Moto
                </label>
                <input
                  type="number"
                  name="price_rent"
                  value={formData.price_rent}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="2500"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzo con Moto Propria
                </label>
                <input
                  type="number"
                  name="price_own"
                  value={formData.price_own}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="1250"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzo RZR Polaris (1 driver)
                </label>
                <input
                  type="number"
                  name="price_rzr_single"
                  value={formData.price_rzr_single}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="4500"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzo RZR Polaris (2 drivers)
                </label>
                <input
                  type="number"
                  name="price_rzr_double"
                  value={formData.price_rzr_double}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="6000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Posti Disponibili */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Posti Disponibili</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posti Disponibili
                </label>
                <input
                  type="number"
                  name="spots_available"
                  value={formData.spots_available}
                  onChange={handleChange}
                  placeholder="12"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posti Totali
                </label>
                <input
                  type="number"
                  name="spots_total"
                  value={formData.spots_total}
                  onChange={handleChange}
                  placeholder="12"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Immagine */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Immagine Evento</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carica foto dal computer
              </label>
              <div className="flex items-center gap-4 flex-wrap">
                <label
                  htmlFor="image-file-input"
                  className={`inline-flex items-center px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${uploading ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                  data-testid="upload-image-btn"
                >
                  <Upload className="mr-2" size={18} />
                  {uploading ? 'Caricamento...' : 'Scegli foto'}
                </label>
                <input
                  id="image-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="upload-image-input"
                />
                <button
                  type="button"
                  onClick={openGallery}
                  className="inline-flex items-center px-4 py-3 rounded-lg font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                  data-testid="pick-from-gallery-btn"
                >
                  <ImageIcon className="mr-2" size={18} />
                  Scegli dalla galleria
                </button>
                {uploadError && (
                  <span className="text-red-600 text-sm" data-testid="upload-image-error">{uploadError}</span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Formati supportati: JPG, PNG, WEBP, GIF — max 15MB. La foto verrà caricata su un archivio sicuro.
              </p>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Oppure inserisci URL immagine *
                </label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  required
                  placeholder="/api/files/offroaditalia/events/... oppure /images/events/evento.jpg"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="event-image-url-input"
                />
              </div>

              {formData.image_url && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Anteprima:</p>
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-64 h-40 object-cover rounded-lg border"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/256x160?text=Immagine+non+trovata'}
                    data-testid="event-image-preview"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Descrizioni */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Descrizioni</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrizione (Italiano) *
                </label>
                <textarea
                  name="description_it"
                  value={formData.description_it}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrizione dettagliata dell'evento..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrizione (English)
                </label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Itinerario Dettagliato (opzionale)
                </label>
                <textarea
                  name="itinerary"
                  value={formData.itinerary}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Giorno 1: ...\nGiorno 2: ..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link SumUp / Pagamento carta (opzionale)
                </label>
                <input
                  type="url"
                  name="sumup_url"
                  value={formData.sumup_url || ''}
                  onChange={handleChange}
                  placeholder="https://pay.sumup.com/b2c/XXXXXXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="event-sumup-input"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Link specifico per il pagamento di questo evento. Se vuoto, viene usato il link predefinito impostato in &quot;Impostazioni Pagamento&quot;.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Importo Acconto (€)
                </label>
                <input
                  type="number"
                  name="deposit_amount"
                  value={formData.deposit_amount || ''}
                  onChange={handleChange}
                  min="0"
                  step="10"
                  placeholder="Es. 500"
                  className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="event-deposit-input"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Quota da versare per bloccare il posto. Apparirà nel modulo di iscrizione come &quot;Paga acconto €XXX&quot;.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplemento Camera Singola (€)
                </label>
                <input
                  type="number"
                  name="single_room_supplement"
                  value={formData.single_room_supplement || ''}
                  onChange={handleChange}
                  min="0"
                  step="10"
                  placeholder="Es. 150"
                  className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="event-single-room-supplement-input"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Supplemento per camera singola (es: €300 Cannonball, €150 Libia/Tunisia 6 tappe, €100 Tunisia 3 tappe, €20 eventi 2 giorni).
                </p>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end space-x-4">
            <Link
              to="/admin/dashboard"
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Annulla
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="mr-2" size={20} />
                  {isEdit ? 'Salva Modifiche' : 'Crea Evento'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Gallery Picker Modal */}
      {showGallery && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowGallery(false)}
          data-testid="gallery-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">Seleziona una foto</h3>
              <button
                type="button"
                onClick={() => setShowGallery(false)}
                className="text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100"
                data-testid="close-gallery-btn"
              >
                <X size={22} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              {galleryLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : galleryItems.length === 0 ? (
                <div className="text-center py-12 text-gray-600">
                  <ImageIcon size={40} className="mx-auto text-gray-400 mb-3" />
                  <p className="mb-1">Nessuna foto in galleria.</p>
                  <p className="text-sm">Carica una foto con &quot;Scegli foto&quot; oppure dalla pagina Galleria Foto.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {galleryItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => pickFromGallery(item)}
                      className="group rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all text-left"
                      data-testid={`gallery-pick-${item.id}`}
                    >
                      <div className="aspect-square bg-gray-100 overflow-hidden">
                        <img
                          src={item.image_url}
                          alt={item.original_filename}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                      <div className="px-2 py-2">
                        <p className="text-xs text-gray-700 truncate" title={item.original_filename}>
                          {item.original_filename}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEventForm;
